import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { Link } from 'react-router-dom';
import { ASSIGNMENTS } from '@/constants/testIds';
import { Copy, WarningOctagon, Trash, XCircle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import ConfirmDialog from '@/components/ConfirmDialog';
import { MemberName } from '@/components/Present';

const STATUS_FILTERS = [
    { value: 'all', label: 'All statuses' },
    { value: 'waiting', label: 'Waiting to be answered' },
    { value: 'answered', label: 'Answered' },
    { value: 'cancelled', label: 'Cancelled' },
];

function statusOf(a) {
    if (a.status === 'Cancelled') return 'cancelled';
    if (a.status === 'Completed' || a.status === 'Reviewed') return 'answered';
    return 'waiting';
}

export default function SentCheckIns() {
    const qc = useQueryClient();
    const { data: assignments = [] } = useQuery({ queryKey: ['assignments'], queryFn: async () => (await api.get('/assignments')).data });
    const [del, setDel] = useState(null);
    const [cancelTarget, setCancelTarget] = useState(null);
    const [memberFilter, setMemberFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');

    const members = useMemo(() => {
        const m = new Map();
        for (const a of assignments) {
            if (a.patient_id && !m.has(a.patient_id)) m.set(a.patient_id, a.patient_name || a.patient_id);
        }
        return Array.from(m.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
    }, [assignments]);

    const filtered = useMemo(() => {
        return assignments.filter(a => {
            if (memberFilter !== 'all' && a.patient_id !== memberFilter) return false;
            if (statusFilter !== 'all' && statusOf(a) !== statusFilter) return false;
            return true;
        });
    }, [assignments, memberFilter, statusFilter]);

    const doDelete = async () => {
        try {
            await api.delete(`/assignments/${del.assignment_id}`);
            toast.success('Removed');
            setDel(null);
            qc.invalidateQueries({ queryKey: ['assignments'] });
        } catch { toast.error('Failed'); }
    };

    const doCancel = async () => {
        try {
            await api.post(`/assignments/${cancelTarget.assignment_id}/cancel`);
            toast.success('Check-in cancelled');
            setCancelTarget(null);
            qc.invalidateQueries({ queryKey: ['assignments'] });
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not cancel');
        }
    };

    return (
        <div data-testid={ASSIGNMENTS.root} className="px-4 sm:px-6 md:px-10 py-6 md:py-8 max-w-7xl">
            <header className="mb-6">
                <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Sent</div>
                <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl text-slate-900 mt-1">Sent check-ins</h1>
                <p className="text-slate-600 mt-2 text-sm md:text-base">Every check-in you&apos;ve shared, and how your loved one answered.</p>
            </header>

            <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 mb-4">
                <div className="w-full sm:min-w-[220px] sm:w-auto">
                    <Select value={memberFilter} onValueChange={setMemberFilter}>
                        <SelectTrigger data-testid="sent-check-ins-member-filter"><SelectValue placeholder="All family members" /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All family members</SelectItem>
                            {members.map(m => (
                                <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="w-full sm:min-w-[220px] sm:w-auto">
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                        <SelectTrigger data-testid="sent-check-ins-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
                        <SelectContent>
                            {STATUS_FILTERS.map(s => (
                                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
                <div className="text-[12px] text-slate-500 sm:ml-auto" data-testid="sent-check-ins-count">
                    {filtered.length} of {assignments.length}
                </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-card shadow-sm overflow-hidden">
                <div className="hidden md:grid grid-cols-[1fr_1fr_1fr_160px_220px] px-5 py-3 border-b border-slate-200 text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                    <div>Check-in</div><div>Family member</div><div>Sent</div><div>Status</div><div className="text-right">Actions</div>
                </div>
                {filtered.length === 0 && <div className="px-5 py-10 text-center text-sm text-slate-500">Nothing matches these filters.</div>}
                <div className="divide-y divide-slate-200">
                    {filtered.map(a => {
                        const url = `${window.location.origin}/check-in/${a.token}`;
                        const s = statusOf(a);
                        const label = s === 'cancelled' ? 'Cancelled'
                            : s === 'answered' ? (a.status === 'Reviewed' ? 'Reviewed' : (a.flagged ? 'Needs attention' : 'Answered'))
                            : 'Waiting to be answered';
                        const cls = s === 'cancelled' ? 'bg-slate-100 text-slate-600'
                            : s === 'answered' ? (a.status === 'Reviewed' ? 'bg-emerald-50 text-emerald-700' : (a.flagged ? 'bg-[#F59E0B] text-black' : 'bg-emerald-50 text-emerald-800'))
                            : 'bg-amber-50 text-amber-900';
                        const actions = (
                            <>
                                {s === 'waiting' && (
                                    <>
                                        <Button size="sm" variant="outline" data-testid={ASSIGNMENTS.open}
                                            onClick={() => { navigator.clipboard.writeText(url); toast.success('Link copied'); }} className="rounded-full min-h-[44px] sm:min-h-0">
                                            <Copy size={14} className="mr-1" /> Copy link
                                        </Button>
                                        <Button size="sm" variant="outline"
                                            onClick={() => setCancelTarget(a)}
                                            data-testid="sent-check-in-cancel"
                                            className="rounded-full text-amber-700 hover:text-amber-800 hover:bg-amber-50 min-h-[44px] sm:min-h-0">
                                            <XCircle size={14} className="mr-1" /> Cancel
                                        </Button>
                                    </>
                                )}
                                <Button size="sm" variant="outline" data-testid={ASSIGNMENTS.delete}
                                    onClick={() => setDel(a)} className="rounded-full text-red-600 hover:text-red-700 hover:bg-red-50 min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0">
                                    <Trash size={14} />
                                </Button>
                            </>
                        );
                        return (
                            <div key={a.assignment_id} data-testid={ASSIGNMENTS.row}>
                                {/* Desktop row */}
                                <div className="hidden md:grid grid-cols-[1fr_1fr_1fr_160px_220px] px-5 py-3 items-center text-sm">
                                    <div className="font-medium text-slate-900">{a.survey_name}</div>
                                    <div className="text-slate-700"><Link to={`/members/${a.patient_id}`} className="hover:underline"><MemberName name={a.patient_name} /></Link></div>
                                    <div className="text-slate-500">{new Date(a.assigned_at).toLocaleString()}</div>
                                    <div>
                                        <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded ${cls}`}>
                                            {a.flagged && a.status !== 'Reviewed' && a.status !== 'Cancelled' && <WarningOctagon size={10} weight="fill" className="inline mr-1" />}
                                            {label}
                                        </span>
                                    </div>
                                    <div className="text-right flex items-center justify-end gap-2">
                                        {actions}
                                    </div>
                                </div>
                                {/* Mobile card */}
                                <div className="md:hidden px-4 py-4 space-y-2">
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <div className="font-medium text-slate-900 text-sm">{a.survey_name}</div>
                                            <Link to={`/members/${a.patient_id}`} className="text-sm text-slate-700 hover:underline"><MemberName name={a.patient_name} /></Link>
                                        </div>
                                        <span className={`text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded shrink-0 ${cls}`}>
                                            {a.flagged && a.status !== 'Reviewed' && a.status !== 'Cancelled' && <WarningOctagon size={10} weight="fill" className="inline mr-1" />}
                                            {label}
                                        </span>
                                    </div>
                                    <div className="text-[11px] text-slate-500">Sent {new Date(a.assigned_at).toLocaleString()}</div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        {actions}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <ConfirmDialog
                open={!!del} onOpenChange={(v) => !v && setDel(null)}
                title="Remove this sent check-in?"
                description={del ? `The link for "${del.survey_name}" will stop working. This can't be undone.` : ''}
                onConfirm={doDelete}
            />
            <ConfirmDialog
                open={!!cancelTarget} onOpenChange={(v) => !v && setCancelTarget(null)}
                title="Cancel this check-in?"
                description={cancelTarget ? `${cancelTarget.patient_name || 'They'} won't be able to answer it.` : ''}
                confirmLabel="Yes, cancel it"
                onConfirm={doCancel}
            />
        </div>
    );
}
